import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Layout } from "./components/Layout";
import { ApiError, createApi } from "./lib/api";
import { useAuth } from "./lib/auth";
import type { Me } from "./lib/types";
import { Admin } from "./pages/Admin";
import { Approve } from "./pages/Approve";
import { Csat } from "./pages/Csat";
import { Directory } from "./pages/Directory";
import { Documents } from "./pages/Documents";
import { Faq } from "./pages/Faq";
import { Flow } from "./pages/Flow";
import { Govern } from "./pages/Govern";
import { Login } from "./pages/Login";
import { Licenses } from "./pages/Licenses";
import { NotProvisioned } from "./pages/NotProvisioned";
import { Support } from "./pages/Support";
import { Tickets } from "./pages/Tickets";

type MeState =
  | { status: "loading" }
  | { status: "ready"; me: Me }
  | { status: "not_provisioned" }
  | { status: "error"; message: string };

export function App() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApi(getToken), [getToken]);

  // The approval page is public + token-gated — it must render outside the auth gate
  // so support can act on the emailed link without a portal session. §scope change
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/approve" element={<Approve api={api} />} />
        <Route path="/csat" element={<Csat api={api} />} />
        <Route path="*" element={<AuthedApp />} />
      </Routes>
    </ErrorBoundary>
  );
}

function AuthedApp() {
  const { ready, user, getToken } = useAuth();
  const [meState, setMeState] = useState<MeState>({ status: "loading" });
  const api = useMemo(
    () =>
      createApi(getToken, "/api", () => {
        setMeState((current) =>
          current.status === "ready"
            ? { status: "ready", me: { ...current.me, mfaRequired: true, mfaVerified: false } }
            : current,
        );
      }),
    [getToken],
  );

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      setMeState({ status: "loading" });
      return;
    }
    let cancelled = false;
    setMeState({ status: "loading" });
    api
      .me()
      .then((me) => {
        if (!cancelled) setMeState({ status: "ready", me });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 403 || err.status === 404) && err.code === "not_provisioned") {
          setMeState({ status: "not_provisioned" });
        } else if (err instanceof ApiError && err.status === 403) {
          setMeState({ status: "not_provisioned" });
        } else {
          setMeState({ status: "error", message: (err as Error).message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, user, api]);

  if (!ready) return <FullScreen>Loading…</FullScreen>;
  if (!user) return <Login />;

  if (meState.status === "loading") return <FullScreen>Resolving your account…</FullScreen>;
  if (meState.status === "not_provisioned")
    return (
      <NotProvisioned
        api={api}
        email={user.email ?? undefined}
        displayName={user.displayName ?? undefined}
      />
    );
  if (meState.status === "error") {
    return <FullScreen>Could not load your session: {meState.message}</FullScreen>;
  }

  const me = meState.me;

  // Email-code second factor for every portal user (§F-8). The backend gates protected API
  // calls until the emailed code is verified and surfaces `mfaRequired` on /me; block the whole
  // portal behind the code-entry screen, then re-resolve the session after verification.
  if (me.mfaRequired) {
    return (
      <MfaGate
        api={api}
        email={me.email}
        onVerified={() => {
          api
            .me()
            .then((next) => setMeState({ status: "ready", me: next }))
            .catch((err) => setMeState({ status: "error", message: (err as Error).message }));
        }}
      />
    );
  }

  return (
    <Layout me={me} api={api}>
      <Routes>
        <Route path="/app/builds" element={<Flow api={api} />} />
        <Route path="/app/documents" element={<Documents api={api} />} />
        <Route path="/app/licenses" element={<Licenses api={api} />} />
        <Route path="/app/faq" element={<Faq api={api} />} />
        <Route path="/app/logs" element={<Govern api={api} />} />
        <Route path="/app/tickets" element={<Tickets api={api} meEmail={me.email} />} />
        <Route
          path="/app/support"
          element={
            me.role === "platform_admin" ? (
              <Support api={api} meEmail={me.email} />
            ) : (
              <Navigate to="/app/builds" replace />
            )
          }
        />
        <Route
          path="/app/directory"
          element={me.role === "platform_admin" ? <Directory api={api} /> : <Navigate to="/app/builds" replace />}
        />
        <Route
          path="/app/admin/*"
          element={me.role === "platform_admin" ? <Admin api={api} /> : <Navigate to="/app/builds" replace />}
        />
        <Route path="*" element={<Navigate to="/app/builds" replace />} />
      </Routes>
    </Layout>
  );
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16 text-sm text-ink-muted">
      {children}
    </div>
  );
}

/**
 * Email-code second-factor entry (§F-8). On mount it asks the backend to email a fresh code, then
 * lets the user submit it. A successful verify calls `onVerified`, which re-resolves /me and drops
 * the gate. "Resend" issues a new code (invalidating the previous one).
 */
function MfaGate({
  api,
  email,
  onVerified,
}: {
  api: ReturnType<typeof createApi>;
  email: string;
  onVerified: () => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(true);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialRequestStarted = useRef(false);

  useEffect(() => {
    if (initialRequestStarted.current) return;
    initialRequestStarted.current = true;
    api
      .mfaStart()
      .then(() => {
        setSent(true);
        setBusy(false);
      })
      .catch((err) => {
        setError((err as Error).message);
        setBusy(false);
      });
  }, [api]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.mfaVerify(code.trim());
      onVerified();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      await api.mfaStart();
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-lg border border-line bg-surface p-6 shadow-sm"
      >
        <h1 className="mb-1 text-lg font-semibold text-ink">Verify it's you</h1>
        <p className="mb-4 text-sm text-ink-muted">
          {sent
            ? `We emailed a 6-digit code to ${email}. Enter it to open the portal.`
            : `Sending a 6-digit code to ${email}…`}
        </p>
        {error && (
          <div className="mb-3 rounded border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}
        <input
          className="input mb-3 w-full tracking-widest"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          autoFocus
        />
        <button className="btn-primary w-full" type="submit" disabled={busy || code.length !== 6}>
          {busy ? "Verifying…" : "Verify"}
        </button>
        <button
          className="btn-ghost mt-2 w-full"
          type="button"
          disabled={busy}
          onClick={() => void resend()}
        >
          Resend code
        </button>
      </form>
    </div>
  );
}
